<template>
    <teleport to="body">
        <transition name="fade">
            <div
                class="fixed inset-0 flex bg-slate-800/50 dark:bg-slate-950/80 backdrop-blur-sm"
                :class="[
                    fullScreenMobile
                        ? 'items-start justify-center sm:items-center sm:justify-center'
                        : 'items-center justify-center',
                ]"
                :style="`z-index: ${zIndex}`"
                role="dialog"
                aria-labelledby="modal-title"
                :aria-hidden="!isOpen"
                v-show="isOpen"
            >
                <div
                    class="w-full relative flex flex-col items-center"
                    :class="[
                        fullScreenMobile
                            ? 'justify-start sm:justify-center'
                            : 'justify-center',
                    ]"
                    :style="{ ...modalStyle, ...maxWidth, ...maxHeight }"
                >
                    <div
                        class="flex flex-col relative bg-white dark:bg-slate-800 shadow-xl dark:ring-1 dark:ring-slate-700"
                        :class="[
                            fullScreenMobile
                                ? 'w-full h-screen max-h-screen rounded-none sm:w-[95%] sm:h-auto sm:max-h-[95vh] sm:rounded-lg sm:min-h-28'
                                : 'w-[95%] lg:w-full min-h-28 max-h-[95vh] rounded-lg',
                            overflowVisible ? 'overflow-visible' : '',
                            modalClass,
                        ]"
                    >
                        <div
                            :class="[
                                noPadding
                                    ? 'sticky bottom-0 z-30'
                                    : 'ui-modal-header pr-10',
                            ]"
                        >
                            <slot name="title">
                                <div class="flex items-start">
                                    <div
                                        class="w-12 h-12 rounded-full flex flex-col justify-center items-center mr-3 shrink-0"
                                        :class="`bg-${iconColor}-100`"
                                        v-if="icon"
                                    >
                                        <Icon
                                            :icon="icon"
                                            :path-class="`fill-${iconColor}-500 dark:fill-slate-400`"
                                        />
                                    </div>
                                    <div class="flex-1">
                                        <h5
                                            id="modal-title"
                                            class="ui-modal-title"
                                            v-if="title"
                                        >
                                            {{ title }}
                                        </h5>
                                        <p
                                            v-if="description"
                                            class="text-slate-500 dark:text-slate-400 text-sm"
                                        >
                                            {{ description }}
                                        </p>
                                        <slot name="subtitle">
                                            <div
                                                class="text-slate-500 dark:text-slate-400 text-sm leading-5 font-normal mt-1"
                                            ></div>
                                        </slot>
                                    </div>
                                </div>
                            </slot>
                            <slot name="close">
                                <button
                                    type="button"
                                    class="z-10 w-10 rounded-lg h-10 justify-center items-center absolute right-5 top-4 focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200 dark:focus:ring-slate-600"
                                    @click="handleClose"
                                    tabindex="1"
                                >
                                    <icon
                                        icon="x"
                                        path-class="stroke-slate-500 dark:stroke-slate-400 hover:stroke-slate-700 dark:hover:stroke-slate-300"
                                        svg-class="fill-slate-500 dark:fill-slate-400 hover:fill-slate-700 dark:hover:fill-slate-300 h-5 w-5"
                                    />
                                    <span class="sr-only">Close</span>
                                </button>
                            </slot>
                        </div>
                        <!-- slot for modal contents: -->
                        <div
                            :class="[
                                noPadding ? 'bg-white dark:bg-slate-800' : 'ui-modal-content',
                                overflowVisible
                                    ? 'overflow-visible'
                                    : 'overflow-y-auto',
                                fullScreenMobile ? 'flex-1' : '',
                            ]"
                            id="ui-modal-content"
                        >
                            <slot name="content"></slot>
                        </div>
                        <div class="ui-modal-errors">
                            <slot name="errors"></slot>
                        </div>
                        <!-- slot for modal footer: -->
                        <div
                            :class="
                                noPadding
                                    ? 'bg-white dark:bg-slate-800 sticky bottom-0 z-30 pt-3 pb-6 rounded-b-lg'
                                    : 'ui-modal-footer'
                            "
                        >
                            <slot name="footer"></slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>
<script>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAppStore } from '@/store/app';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    emits: ['close'],
    components: {
        Icon,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        icon: {
            type: String,
            default: null,
        },
        iconColor: {
            type: String,
            default: 'slate',
        },
        width: {
            type: [String, Number],
            default: '800px',
        },
        hasAutoflow: {
            type: Boolean,
            default: true,
        },
        errors: {
            type: Array,
            default: () => [],
        },
        isTeleported: {
            type: Boolean,
            default: false,
        },
        zIndex: {
            type: Number,
            default: 9999,
        },
        fullScreenMobile: {
            type: Boolean,
            default: false,
        },
        overflowVisible: {
            type: Boolean,
            default: false,
        },
        modalClass: {
            type: String,
            default: '',
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const viewportWidth = ref(0);
        const viewportHeight = ref(0);

        const appStore = useAppStore();
        const { sidebarOpen } = storeToRefs(appStore);

        const isMobileFullScreen = computed(
            () =>
                props.fullScreenMobile &&
                viewportWidth.value > 0 &&
                viewportWidth.value < 640
        );

        const maxWidth = computed(() => {
            if (isMobileFullScreen.value) return {};
            return { maxWidth: `${props.width}` };
        });

        const maxHeight = computed(() => {
            if (isMobileFullScreen.value)
                return { height: '100dvh', maxHeight: '100dvh' };
            return { maxHeight: '95dvh' };
        });

        const modalStyle = computed(() => {
            const sidebarWidth = sidebarOpen.value ? '128px' : '0px';

            return {
                transform: props.isTeleported
                    ? 'translateX(0)'
                    : `translateX(${sidebarWidth})`,
            };
        });

        const handleClose = () => {
            emit('close');
        };

        const updateViewportSize = () => {
            viewportWidth.value = window.innerWidth;
            viewportHeight.value = window.innerHeight;
        };

        onMounted(() => {
            updateViewportSize();
            window.addEventListener('resize', updateViewportSize);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', updateViewportSize);
        });

        return {
            handleClose,
            maxHeight,
            maxWidth,
            modalStyle,
        };
    },
};
</script>
